import { TaskPublishingButton } from 'components/CommonComponents/TaskPublishingOverlay'
import { Button, CloseButton } from 'components/ui'
import styles from './Header.module.css'
import React, { useCallback, useMemo, useState } from 'react'
import { Flex, Text } from '@chakra-ui/core'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { RootState } from 'constants/interfaces'
import useLocaleText from 'components/useLocaleText'
import { StepType, useFormStepContext } from '../FormStepContext'
import LocaleText from 'components/LocaleText'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import StepperProgress from './StepperProgress'
import { AIStepType, useAILoadingContext } from 'components/TasksV2/AITaskCreation/AIFormStepContext'
import { VM_AI_STEPS } from '../hooks/useVmAiEnhance'
import { useCreationText } from '../hooks/useCreationText'

export const Header = ({
  onClose,
  builder = false,
  settings = false,
  titleSlotRef,
  onBack,
  extraActions,
  navigationDisabled = false,
}: {
  onClose: () => void
  onBack?: () => void
  extraActions?: React.ReactNode
  navigationDisabled?: boolean
  builder?: boolean
  settings?: boolean
  titleSlotRef?: (node: HTMLDivElement | null) => void
}) => {
  const creationText = useCreationText()
  const isRTL = useSelector(isRtlSelector)
  const theme = useSelector((state: RootState) => state.general.theme)
  const isLoading = useSelector((state: RootState) => state.tasks_v2.isLoading)
  const t_next_step = useLocaleText('t_next_step')
  const t_createTask = useLocaleText('post')
  const t_save = useLocaleText('save')
  const editTitle = useLocaleText('t_edit_task')
  const t_back = useLocaleText('header_button_back')
  const t_cancel = useLocaleText('cancel')

  const card_confirm_text = useLocaleText('card_confirm_text')
  const card_confirm_desc = useLocaleText('card_confirm_desc')
  const yes = useLocaleText('yes')
  const no = useLocaleText('no')

  const { sumbitStep, goToPreviousStep, stepStack, isMediaUploading, isVmTask, isEdit } = useFormStepContext()
  const { isGenerating: generatingTask } = useAILoadingContext()
  const { isSubtaskAiGenerating, isSubmitting } = useFormStepContext()

  const isGenerating = generatingTask || isSubtaskAiGenerating || isSubmitting

  const [isConfirmPopupVisible, setIsConfirmPopupVisible] = useState(false)

  const hadnlePreviousStep = useCallback(() => {
    if (isGenerating) return
    if (stepStack.length === 1) {
      setIsConfirmPopupVisible(true)
    } else {
      goToPreviousStep()
    }
  }, [goToPreviousStep, stepStack.length, isGenerating])

  const handleCloseConfirm = useCallback(() => {
    onClose()
    setIsConfirmPopupVisible(false)
  }, [onClose])

  const handleCancelConfirm = useCallback(() => {
    setIsConfirmPopupVisible(false)
  }, [])

  const currentStep = useMemo(() => stepStack[stepStack.length - 1], [stepStack])

  const isAiStep = VM_AI_STEPS.includes(currentStep)
  const backLiteral = stepStack.length === 1 ? 'cancel' : 'header_button_back'

  const submitButtonType = useMemo(() => {
    switch (currentStep) {
      case StepType.SUBTASK_TASK_CREATION:
        return (
          <Button type="button" variant="ghost" height="auto" isDisabled={isMediaUploading} onClick={sumbitStep}>
            <Text fontSize="14px">{t_next_step}</Text>
            {isRTL ? <ChevronLeft /> : <ChevronRight />}
          </Button>
        )
      case StepType.TASK_SETTINGS:
        return (
          <Button
            type="button"
            bg={theme?.elementsColor}
            color="white"
            _focus={{ outline: 'none' }}
            borderRadius="20px"
            onClick={sumbitStep}
            isLoading={isLoading}
          >
            <Text>{t_createTask}</Text>
          </Button>
        )
      case StepType.TIME_FRAME_SELECTION:
        return (
          <Button
            type="button"
            bg={theme?.elementsColor}
            color="white"
            _focus={{ outline: 'none' }}
            borderRadius="20px"
            onClick={sumbitStep}
            isLoading={isLoading}
          >
            <Text fontSize="14px">{t_save}</Text>
          </Button>
        )

      default:
        break
    }
  }, [currentStep, isLoading, isMediaUploading, isRTL, sumbitStep, t_createTask, t_next_step, t_save, theme])

  return (
    <Flex
      className={builder || settings ? styles.creationHeader : undefined}
      alignItems="center"
      justifyContent="space-between"
      h={builder || settings ? 'auto' : '60px'}
      minH={builder || settings ? '36px' : undefined}
    >
      {builder || settings ? (
        <>
          {settings && (
            <Button
              type="button"
              variant="icon"
              aria-label={t_back}
              onClick={onBack || hadnlePreviousStep}
              isDisabled={isGenerating || navigationDisabled}
            >
              {isRTL ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </Button>
          )}
          <Text className={styles.title} fontWeight={600}>
            {isEdit ? editTitle : isVmTask ? creationText.vm : creationText.create}
          </Text>
          <div className={styles.creationActions}>
            {!isSubmitting && extraActions}
            <StepperProgress compact />
            <div className={styles.actionSlot} ref={titleSlotRef} />
            {isSubmitting && <TaskPublishingButton />}
          </div>
          <CloseButton
            type="button"
            aria-label={t_cancel}
            isDisabled={isGenerating || isMediaUploading || navigationDisabled}
            onClick={() => setIsConfirmPopupVisible(true)}
          />
        </>
      ) : (
        <>
          <Button
            type="button"
            variant="ghost"
            height="auto"
            onClick={onBack || hadnlePreviousStep}
            isDisabled={isGenerating || navigationDisabled}
          >
            {isRTL ? <ChevronRight /> : <ChevronLeft />}
            <Text fontSize="14px" w="max-content" maxW="100px" isTruncated>
              <LocaleText text={backLiteral} />
            </Text>
          </Button>

          {/* The 2-step task stepper is meaningless inside the AI sub-flow — show its own position instead */}
          {isAiStep ? (
            <Text fontSize="14px" color="#718096">
              {`AI · ${currentStep === AIStepType.AI_SUBTASK ? 2 : 1}/2`}
            </Text>
          ) : (
            <StepperProgress />
          )}
          {isSubmitting ? <TaskPublishingButton /> : submitButtonType}
        </>
      )}

      <ConfirmationPopup
        isOpen={isConfirmPopupVisible}
        onConfirm={handleCloseConfirm}
        onCancel={handleCancelConfirm}
        title={card_confirm_text}
        description={card_confirm_desc}
        cancelButtonText={no}
        confirmButtonText={yes}
        isLoading={false}
      />
    </Flex>
  )
}
